import React, { useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  withStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import ForecastTrendChart from './ForecastTrendChart';
import { CumulativeClosedWonChart, PipelineByStageChart, ForecastAccuracyChart } from './DealProgressionCharts';
import { PipelineAgingChart, ConversionCycletimeChart, QualificationConversionChart, POCConversionChart } from './DealConversionCharts';
import UpsellCrossSellChart from './UpsellCrossSellChart';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';
import GlobalDownloadButton from '../../components/GlobalDownloadButton';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 250,
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  elapsedBadge: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  currentBadge: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  futureBadge: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  summaryCard: {
    backgroundColor: '#fff',
    marginTop: theme.spacing(2),
  },
  summaryItem: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#666',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  currentQuarterInfo: {
    marginTop: theme.spacing(1),
  },
  // Paper 2 Table Styles
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: '#1976d2',
    '& th': {
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.875rem',
      padding: '12px 8px',
      borderRight: '1px solid rgba(255,255,255,0.2)',
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  newOpportunityRow: {
    backgroundColor: '#c8e6c9 !important',
    '&:hover': {
      backgroundColor: '#a5d6a7 !important',
    },
  },
  tableCell: {
    fontSize: '0.813rem',
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  summaryRow: {
    backgroundColor: '#f5f5f5',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  targetRow: {
    backgroundColor: '#fff3cd',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  notesBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fffbea',
    borderRadius: 4,
    border: '1px solid #ffd54f',
  },
  noteItem: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  leftNote: {
    backgroundColor: '#fff9e6',
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: '0.75rem',
    lineHeight: 1.6,
  },
});

const RegionalSalesReviewPackage = (props) => {
  const { classes, forecastTrendData, salesForecastData, dealProgressionData, dealConversionData, upsellCrossSellData } = props;

  useEffect(() => {
    props.getForecastTrendData();
    props.getDealProgressionData();
    props.getDealConversionData();
    props.getUpsellCrossSellData();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'elapsed':
        return classes.elapsedBadge;
      case 'current':
        return classes.currentBadge;
      default:
        return classes.futureBadge;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'elapsed':
        return 'Completed';
      case 'current':
        return 'Current';
      default:
        return 'Future';
    }
  };

  // Mock data for Paper 2 - replace with actual data from API
  const mockForecastData = {
    opportunities: [
      { customer: 'Customer_1', rsm: '', acv: 10, discount: '', closeDate: '', age: '', stage: 'Q/E/N/P', daysInStage: '', partner: '', risks: ['aa', 'bb'], actions: ['cc', 'cc'], isNew: false },
      { customer: 'Customer_2', rsm: '', acv: 10, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_3', rsm: '', acv: 10, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_4', rsm: '', acv: 10, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: true },
      { customer: 'Customer_5', rsm: '', acv: 5, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_6', rsm: '', acv: 10, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_7', rsm: '', acv: 8, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_8', rsm: '', acv: 9, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_9', rsm: '', acv: 12, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
      { customer: 'Customer_10', rsm: '', acv: 20, discount: '', closeDate: '', age: '', stage: '', daysInStage: '', partner: '', risks: [], actions: [], isNew: false },
    ],
    total: 104,
    target: 100,
  };

  if (forecastTrendData === "") {
    return (
      <div className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rect" animation="wave" height={60} width="100%" />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rect" animation="wave" height={40} width="100%" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Skeleton variant="rect" animation="wave" height={300} width="100%" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Skeleton variant="rect" animation="wave" height={300} width="100%" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Skeleton variant="rect" animation="wave" height={300} width="100%" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Skeleton variant="rect" animation="wave" height={300} width="100%" />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      {/* Paper 1: Forecast Trend Charts */}
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <GlobalDownloadButton
          dashboardTitle={`Regional Sales Review Package`}
          fileName={`Regional_Sales_Review_Package`}
        />
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {forecastTrendData?.title || 'Forecast Trend'} ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              Weekly update - Every Friday PM
            </span>
          </Box>
          {/* <Typography className={classes.subtitle}>
            Regional Sales Review Package - Paper 1
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.currentQuarterInfo}>
            Current Quarter: {forecastTrendData?.currentFiscalQuarter || 'N/A'} | Fiscal Year: {forecastTrendData?.fiscalYear || 'N/A'}
          </Typography> */}
        </div>

        {/* Quarterly Charts - 2x2 Grid */}
        <Grid container spacing={3}>
          {forecastTrendData?.quarters?.map((quarter) => (
            <Grid item xs={12} sm={6} md={6} lg={6} key={quarter.quarter}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <div className={classes.chartContainer}>
                    <ForecastTrendChart
                      data={quarter.weeklyData}
                      quarterLabel={quarter.quarter}
                      status={quarter.status}
                      target={quarter.target}
                    />
                  </div>
                  {/* <Box mt={2} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      Won: {countOrACVFormatterMillionFixedOne(quarter.totalWon, true)} | Most Likely: {countOrACVFormatterMillionFixedOne(quarter.totalMostLikely, true)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Target: {countOrACVFormatterMillionFixedOne(quarter.target, true)}
                    </Typography>
                  </Box> */}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Legend */}
        <Box mt={3}>
          <div className={classes.legend}>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#70ad47' }} />
              <Typography variant="body2">Won</Typography>
            </div>
            <div className={classes.legendItem}>
              <div className={classes.legendBox} style={{ backgroundColor: '#c5e0b4' }} />
              <Typography variant="body2">Most Likely</Typography>
            </div>
            <div className={classes.legendItem}>
              <div
                className={classes.legendBox}
                style={{ backgroundColor: '#bf9000', height: 3, width: 24 }}
              />
              <Typography variant="body2">Target</Typography>
            </div>
          </div>
        </Box>
      </Paper>

      {/* Paper 2: Sales Forecast Table */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          {/* Header */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast – Current Quarter – Week x_of_13
            </Typography>
          </Box>
          
      

          <Grid container spacing={2}>
       

            {/* Table */}
            <Grid item xs={12} md={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>RSM</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell align="center">Discount<br/>(%)</TableCell>
                      <TableCell align="center">Close Date</TableCell>
                      <TableCell align="center">AGE<br/>(days)</TableCell>
                      <TableCell align="center">Stage</TableCell>
                      <TableCell align="center">Days in<br/>Stage</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Risks</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockForecastData.opportunities.map((opp, index) => (
                      <TableRow 
                        key={index} 
                        className={opp.isNew ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.customer}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.rsm}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acv}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.discount}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.closeDate}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.age}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.stage}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.daysInStage}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.risks.map((risk, i) => (
                            <div key={i}>{i + 1}) {risk}</div>
                          ))}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.actions.map((action, i) => (
                            <div key={i}>{i + 1}) {action}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Summary Row */}
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.total}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}>
                        <Box display="flex" gap={2} fontSize="0.75rem">
                          <span>$__ m Q, $__ m E, $__ in N, $__ in C</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Target Row */}
                    <TableRow className={classes.targetRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TARGET for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.target}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          

         
        </Paper>
      </Box>
   

     {/* Paper 3: Sales Forecast Table Pulls Ahead*/}
      <Box mt={4}>
        <Paper className={classes.paper}>
          {/* Header */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast –  Current Quarter – Pull aheads – week x_of_13
            </Typography>
          </Box>
          
      

          <Grid container spacing={2}>
       

            {/* Table */}
            <Grid item xs={12} md={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>RSM</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell align="center">Discount<br/>(%)</TableCell>
                      <TableCell align="center">Close Date</TableCell>
                      <TableCell align="center">AGE<br/>(days)</TableCell>
                      <TableCell align="center">Stage</TableCell>
                      <TableCell align="center">Days in<br/>Stage</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Risks</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockForecastData.opportunities.map((opp, index) => (
                      <TableRow 
                        key={index} 
                        className={opp.isNew ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.customer}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.rsm}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acv}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.discount}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.closeDate}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.age}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.stage}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.daysInStage}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.risks.map((risk, i) => (
                            <div key={i}>{i + 1}) {risk}</div>
                          ))}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.actions.map((action, i) => (
                            <div key={i}>{i + 1}) {action}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Summary Row */}
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.total}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}>
                        <Box display="flex" gap={2} fontSize="0.75rem">
                          <span>$__ m Q, $__ m E, $__ in N, $__ in C</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Target Row */}
                    <TableRow className={classes.targetRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TARGET for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.target}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          

         
        </Paper>
      </Box>

        {/* Paper 4: Sales Forecast Table Next Quarter */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          {/* Header */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast –  Next Quarter 
            </Typography>
          </Box>
          
      

          <Grid container spacing={2}>
       

            {/* Table */}
            <Grid item xs={12} md={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>RSM</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell align="center">Discount<br/>(%)</TableCell>
                      <TableCell align="center">Close Date</TableCell>
                      <TableCell align="center">AGE<br/>(days)</TableCell>
                      <TableCell align="center">Stage</TableCell>
                      <TableCell align="center">Days in<br/>Stage</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Risks</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockForecastData.opportunities.map((opp, index) => (
                      <TableRow 
                        key={index} 
                        className={opp.isNew ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.customer}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.rsm}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acv}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.discount}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.closeDate}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.age}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.stage}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.daysInStage}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.risks.map((risk, i) => (
                            <div key={i}>{i + 1}) {risk}</div>
                          ))}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.actions.map((action, i) => (
                            <div key={i}>{i + 1}) {action}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Summary Row */}
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.total}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}>
                        <Box display="flex" gap={2} fontSize="0.75rem">
                          <span>$__ m Q, $__ m E, $__ in N, $__ in C</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Target Row */}
                    <TableRow className={classes.targetRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TARGET for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.target}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          

         
        </Paper>
      </Box>

        {/* Paper 4: Sales Forecast Table Next Quarter + 1 */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          {/* Header */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Sales Forecast –  Next Quarter  + 1
            </Typography>
          </Box>
          
      

          <Grid container spacing={2}>
       

            {/* Table */}
            <Grid item xs={12} md={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>RSM</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell align="center">Discount<br/>(%)</TableCell>
                      <TableCell align="center">Close Date</TableCell>
                      <TableCell align="center">AGE<br/>(days)</TableCell>
                      <TableCell align="center">Stage</TableCell>
                      <TableCell align="center">Days in<br/>Stage</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Risks</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockForecastData.opportunities.map((opp, index) => (
                      <TableRow 
                        key={index} 
                        className={opp.isNew ? classes.newOpportunityRow : classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.customer}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.rsm}</TableCell>
                        <TableCell className={classes.tableCell} align="right">{opp.acv}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.discount}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.closeDate}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.age}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.stage}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{opp.daysInStage}</TableCell>
                        <TableCell className={classes.tableCell}>{opp.partner}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.risks.map((risk, i) => (
                            <div key={i}>{i + 1}) {risk}</div>
                          ))}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {opp.actions.map((action, i) => (
                            <div key={i}>{i + 1}) {action}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Summary Row */}
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.total}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}>
                        <Box display="flex" gap={2} fontSize="0.75rem">
                          <span>$__ m Q, $__ m E, $__ in N, $__ in C</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Target Row */}
                    <TableRow className={classes.targetRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TARGET for this Quarter</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>{mockForecastData.target}</strong>
                      </TableCell>
                      <TableCell colSpan={8} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          

         
        </Paper>
      </Box>

      {/* Paper 6: Deal Progression and Closures */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Deal Progression and Closures
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly and Quarterly update
            </span>
          </Box>

          {dealProgressionData === "" ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={280} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {/* Chart 1: Cumulative Closed Won by Month */}
              <Grid item xs={12} md={12}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <CumulativeClosedWonChart
                        data={dealProgressionData?.cumulativeClosedWon}
                        fiscalYear={dealProgressionData?.fiscalYear}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 2: Pipeline percent by stage */}
              <Grid item xs={12} md={5}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <PipelineByStageChart
                        data={dealProgressionData?.pipelineByStage}
                        currentFiscalQuarter={dealProgressionData?.currentFiscalQuarter}
                        stageCategories={dealProgressionData?.stageCategories}
                      />
                    </div>
                    {/* Legend for stacked bar - Dynamic */}
                    <Box mt={2}>
                      <div className={classes.legend}>
                        {(() => {
                          const colorScale = d3.scaleOrdinal(updatedScheme10);
                          return dealProgressionData?.stageCategories?.map((category, index) => (
                            <div key={category} className={classes.legendItem}>
                              <div
                                className={classes.legendBox}
                                style={{ backgroundColor: colorScale(category) }}
                              />
                              <Typography variant="body2">{category}</Typography>
                            </div>
                          ));
                        })()}
                      </div>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Chart 3: Forecast Accuracy */}
              <Grid item xs={12} md={5}>
                <Card className={classes.chartCard}>
                  <CardContent>
                    <div className={classes.chartContainer}>
                      <ForecastAccuracyChart
                        data={dealProgressionData?.forecastAccuracy}
                        target={dealProgressionData?.forecastAccuracyTarget}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>

            
            </Grid>
          )}
        </Paper>
      </Box>

      {/* Paper 7: Deal Conversion Efficiency */}
     

      {/* Paper 8: Win-Loss Analysis */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Win-Loss analysis: Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              New page for each month at close
            </span>
          </Box>

          <Grid container spacing={2}>
            {/* Won Opportunities Table */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                Won Opportunities
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell>Competition</TableCell>
                      <TableCell>Key Learnings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer A</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.2</TableCell>
                      <TableCell className={classes.tableCell}>Competitor X</TableCell>
                      <TableCell className={classes.tableCell}>Strong technical fit</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer B</TableCell>
                      <TableCell className={classes.tableCell} align="right">0.8</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Existing relationship</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Lost Opportunities Table */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                Lost Opportunities
              </Typography>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">ACV ($M)</TableCell>
                      <TableCell>Competition</TableCell>
                      <TableCell>Key Learnings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer C</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.5</TableCell>
                      <TableCell className={classes.tableCell}>Competitor Y</TableCell>
                      <TableCell className={classes.tableCell}>Price sensitivity</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>Customer D</TableCell>
                      <TableCell className={classes.tableCell} align="right">0.9</TableCell>
                      <TableCell className={classes.tableCell}>Competitor Z</TableCell>
                      <TableCell className={classes.tableCell}>Feature gaps</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

        </Paper>
      </Box>

      {/* Paper 9: MSSP Fulfillment */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              MSSP Fulfillment – Cumulative ACV ($M)
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Box className={classes.chartContainer} style={{ minHeight: 350 }}>
                    <svg width="800" height="300">
                      <text x="400" y="150" textAnchor="middle" fill="#666" fontSize="16">
                        Cumulative Target vs Actual ACV Chart
                      </text>
                      <text x="400" y="180" textAnchor="middle" fill="#999" fontSize="12">
                        (Chart will be populated with actual data)
                      </text>
                    </svg>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Paper>
      </Box>

      {/* Paper 10: Renewals Tracker Summary */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker summary
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={3}>
            {/* Monthly Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Monthly Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <svg width="400" height="250">
                      <text x="200" y="125" textAnchor="middle" fill="#666" fontSize="14">
                        Monthly Target vs Actual
                      </text>
                      <text x="200" y="145" textAnchor="middle" fill="#999" fontSize="11">
                        (Bar chart placeholder)
                      </text>
                    </svg>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Cumulative Renewals Chart */}
            <Grid item xs={12} md={6}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cumulative Renewals ($M)
                  </Typography>
                  <Box className={classes.chartContainer}>
                    <svg width="400" height="250">
                      <text x="200" y="125" textAnchor="middle" fill="#666" fontSize="14">
                        Cumulative Target vs Actual
                      </text>
                      <text x="200" y="145" textAnchor="middle" fill="#999" fontSize="11">
                        (Line chart placeholder)
                      </text>
                    </svg>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Paper>
      </Box>


      {/* Paper 11: Renewals Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Renewals tracker detail – Month of XXX
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>#</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="center">Renewal Date</TableCell>
                      <TableCell align="right">Contract Value ($M)</TableCell>
                      <TableCell align="center">Probability (%)</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Risk Factors</TableCell>
                      <TableCell>Action Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>1</TableCell>
                      <TableCell className={classes.tableCell}>Customer Alpha</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-15</TableCell>
                      <TableCell className={classes.tableCell} align="right">2.5</TableCell>
                      <TableCell className={classes.tableCell} align="center">90</TableCell>
                      <TableCell className={classes.tableCell} align="center">On Track</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Schedule QBR</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>2</TableCell>
                      <TableCell className={classes.tableCell}>Customer Beta</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-20</TableCell>
                      <TableCell className={classes.tableCell} align="right">1.8</TableCell>
                      <TableCell className={classes.tableCell} align="center">75</TableCell>
                      <TableCell className={classes.tableCell} align="center">At Risk</TableCell>
                      <TableCell className={classes.tableCell}>Budget concerns</TableCell>
                      <TableCell className={classes.tableCell}>Executive engagement needed</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>3</TableCell>
                      <TableCell className={classes.tableCell}>Customer Gamma</TableCell>
                      <TableCell className={classes.tableCell} align="center">2026-03-25</TableCell>
                      <TableCell className={classes.tableCell} align="right">3.2</TableCell>
                      <TableCell className={classes.tableCell} align="center">95</TableCell>
                      <TableCell className={classes.tableCell} align="center">Committed</TableCell>
                      <TableCell className={classes.tableCell}>None</TableCell>
                      <TableCell className={classes.tableCell}>Send contract</TableCell>
                    </TableRow>
                    <TableRow className={classes.summaryRow}>
                      <TableCell colSpan={3} className={classes.tableCell}>
                        <strong>TOTAL</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <strong>7.5</strong>
                      </TableCell>
                      <TableCell colSpan={4} className={classes.tableCell}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

        </Paper>
      </Box>


       {/* Paper 12: Upsell-Cross Sell Tracker */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Upsell-Cross Sell Tracker
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          {upsellCrossSellData === "" ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rect" animation="wave" height={300} width="100%" />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {/* Company Chart */}
              {upsellCrossSellData?.teams?.Company && (
                <Grid item xs={12} md={6}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer} id="printChart">
                        <UpsellCrossSellChart
                          data={upsellCrossSellData.teams.Company}
                          teamName="ColorTokens"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* US Chart */}
              {upsellCrossSellData?.teams?.US && (
                <Grid item xs={12} md={6}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer} id="printChart">
                        <UpsellCrossSellChart
                          data={upsellCrossSellData.teams.US}
                          teamName="US"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* APJ + India Chart */}
              {upsellCrossSellData?.teams?.['APJ + India'] && (
                <Grid item xs={12} md={6}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer} id="printChart">
                        <UpsellCrossSellChart
                          data={upsellCrossSellData.teams['APJ + India']}
                          teamName="APJ + India"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* EMEA Chart */}
              {upsellCrossSellData?.teams?.EMEA && (
                <Grid item xs={12} md={6}>
                  <Card className={classes.chartCard}>
                    <CardContent>
                      <div className={classes.chartContainer} id="printChart">
                        <UpsellCrossSellChart
                          data={upsellCrossSellData.teams.EMEA}
                          teamName="EMEA"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}

          {/* Legend for cumulative target vs actual */}
          <Box mt={2}>
            <div className={classes.legend}>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#bf9000' }}
                />
                <Typography variant="body2">Cumulative (target)</Typography>
              </div>
              <div className={classes.legendItem}>
                <div
                  className={classes.legendBox}
                  style={{ backgroundColor: '#70ad47' }}
                />
                <Typography variant="body2">Cumulative (actual)</Typography>
              </div>
            </div>
          </Box>
        </Paper>
      </Box>

      {/* Paper 13: Upsell-Cross Sell Tracker Detail */}
      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              Upsell-Cross Sell tracker detail
            </Typography>
            <span className={classes.updateFrequency}>
              Monthly update
            </span>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer className={classes.tableContainer} style={{ overflowX: 'auto' }}>
                <Table size="small" style={{ minWidth: '2000px' }}>
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>February</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>March</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>April</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>May</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>June</TableCell>
                      <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>July</TableCell>
                    </TableRow>
                    <TableRow className={classes.tableHeader}>
                      <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Target</TableCell>
                      <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3, 4, 5, 6].map((rowNum) => (
                      <TableRow key={rowNum} className={classes.tableRow}>
                        <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}></TableCell>
                        {[...Array(6)].map((_, monthIdx) => (
                          <React.Fragment key={monthIdx}>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className={classes.summaryRow}>
                      <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                        <strong>TOTAL =</strong>
                      </TableCell>
                      {[...Array(6)].map((_, monthIdx) => (
                        <React.Fragment key={monthIdx}>
                          <TableCell className={classes.tableCell}></TableCell>
                          <TableCell className={classes.tableCell}></TableCell>
                          <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          <Box mt={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TableContainer className={classes.tableContainer} style={{ overflowX: 'auto' }}>
                  <Table size="small" style={{ minWidth: '2000px' }}>
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>August</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>September</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>October</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>November</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>December</TableCell>
                        <TableCell align="center" colSpan={3} style={{ borderRight: '2px solid #ddd' }}>January</TableCell>
                      </TableRow>
                      <TableRow className={classes.tableHeader}>
                        <TableCell align="center" style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 2 }}></TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Target</TableCell>
                        <TableCell align="center" style={{ borderRight: '2px solid #ddd' }}>Actual</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[1, 2, 3, 4, 5, 6].map((rowNum) => (
                        <TableRow key={rowNum} className={classes.tableRow}>
                          <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}></TableCell>
                          {[...Array(6)].map((_, monthIdx) => (
                            <React.Fragment key={monthIdx}>
                              <TableCell className={classes.tableCell}></TableCell>
                              <TableCell className={classes.tableCell}></TableCell>
                              <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      ))}
                      <TableRow className={classes.summaryRow}>
                        <TableCell className={classes.tableCell} style={{ position: 'sticky', left: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                          <strong>TOTAL =</strong>
                        </TableCell>
                        {[...Array(6)].map((_, monthIdx) => (
                          <React.Fragment key={monthIdx}>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell}></TableCell>
                            <TableCell className={classes.tableCell} style={{ borderRight: monthIdx < 5 ? '2px solid #ddd' : 'none' }}></TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>

        </Paper>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, forecastTrendData, salesForecastData, dealProgressionData, dealConversionData, upsellCrossSellData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    forecastTrendData,
    salesForecastData,
    dealProgressionData,
    dealConversionData,
    upsellCrossSellData,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getForecastTrendData: () => {
    dispatch({ type: "get_forecast_trend_data_request" });
    appService.getRegionalSalesReviewForecastTrend().then(
      (json) => {
        dispatch({ type: "get_forecast_trend_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_data_failure", error });
      }
    );
  },
  getDealProgressionData: () => {
    dispatch({ type: "get_deal_progression_data_request" });
    appService.getRegionalSalesReviewDealProgression().then(
      (json) => {
        dispatch({ type: "get_deal_progression_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_progression_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_progression_data_failure", error });
      }
    );
  },
  getDealConversionData: () => {
    dispatch({ type: "get_deal_conversion_data_request" });
    appService.getRegionalSalesReviewDealConversion().then(
      (json) => {
        dispatch({ type: "get_deal_conversion_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_conversion_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_conversion_data_failure", error });
      }
    );
  },
  getUpsellCrossSellData: () => {
    dispatch({ type: "get_upsell_cross_sell_data_request" });
    appService.getRegionalSalesReviewUpsellCrossSell().then(
      (json) => {
        dispatch({ type: "get_upsell_cross_sell_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_upsell_cross_sell_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_upsell_cross_sell_data_failure", error });
      }
    );
  },
});

const connectedRegionalSalesReviewPackage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalSalesReviewPackage));

export default connectedRegionalSalesReviewPackage;